"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/features/auth/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  Check,
  Clock,
  Edit2,
  ExternalLink,
  User
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentsSection } from "@/features/applications/components";
import { FileUploader } from "@/components/features/FileUploader";
import { useNavigate } from "react-router-dom";

interface ApplicationStep {
  id: string;
  title: string;
  description: string;
  status: "not_started" | "in_progress" | "completed" | "pending_review";
  href: string;
  estimatedTime: string;
}

interface TimelineStepProps {
  step: ApplicationStep;
  id: string;
  stepNumber: number;
  isLast: boolean;
  isAccessible: boolean;
  isNextStepCompleted?: boolean;
  isNextStep?: boolean;
  onEdit?: () => void;
}

const TimelineStep: React.FC<TimelineStepProps> = ({
  step,
  stepNumber,
  isLast,
  isAccessible,
  id,
  isNextStepCompleted = false,
  isNextStep = false
}) => {
  const { t } = useTranslation(["talent", "common"]);
  const isCompleted = step.status === "completed";
  const isInProgress = step.status === "in_progress";
  const isPendingReview = step.status === "pending_review";

  const getStatusIcon = (status: ApplicationStep["status"]) => {
    switch (status) {
      case "completed":
        return <Check className="h-4 w-4 text-white" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-blue-600" />;
      case "pending_review":
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return (
          <span
            className={cn(
              "text-sm font-medium",
              isNextStep ? "text-blue-600 font-semibold" : "text-gray-500"
            )}
          >
            {stepNumber}
          </span>
        );
    }
  };

  const getStatusBadge = (status: ApplicationStep["status"]) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-800">
            {t("talent:application.status.completed")}
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-[#d769b3]/10 text-[#d769b3]">
            {t("talent:application.status.in_progress")}
          </Badge>
        );
      case "pending_review":
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            {t("talent:application.status.pending_review")}
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {t("talent:application.status.not_started")}
          </Badge>
        );
    }
  };

  const handleStepClick = () => {
    // Navigate to the step or trigger edit action
    if (isAccessible) {
      window.location.href = step.href;
    }
  };

  return (
    <div className="relative">
      {/* Vertical Line */}
      {!isLast && (
        <div className="absolute left-4 top-8 md:left-6 md:top-12 w-px md:w-0.5 h-full bg-gray-200 z-0" />
      )}
      {!isLast && isCompleted && isNextStepCompleted && (
        <div className="absolute left-4 top-8 md:left-6 md:top-12 w-px md:w-0.5 h-full bg-green-600 z-0" />
      )}

      <div className="flex items-start gap-3 md:gap-6 pb-8">
        {/* Step Circle */}
        <div className="relative group">
          <div
            id={id}
            className={cn(
              "relative z-10 flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full transition-all flex-shrink-0 cursor-pointer border-2",
              isCompleted
                ? "bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700"
                : isInProgress
                  ? "bg-[#d769b3] border-[#d769b3] hover:bg-[#c055a0] hover:border-[#c055a0]"
                  : isPendingReview
                    ? "bg-yellow-500 border-yellow-500 hover:bg-yellow-600 hover:border-yellow-600"
                    : isNextStep
                      ? "bg-[#d769b3]/10 border-[#d769b3]/30 hover:bg-[#d769b3]/20 hover:border-[#d769b3]/50 shadow-sm"
                      : "bg-white border-gray-300 hover:border-gray-400"
            )}
            onClick={handleStepClick}
          >
            {getStatusIcon(step.status)}
          </div>

          {/* Hover Tooltip */}
          <div className="absolute top-1/2 left-12 md:left-16 -translate-y-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
            {isCompleted ? "Schritt bearbeiten" : "Schritt starten"}
          </div>
        </div>

        {/* Step Content - Now Clickable */}
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "bg-white border rounded-lg p-3 md:p-6 hover:shadow-md transition-all cursor-pointer group relative",
              isNextStep &&
                "border-[#d769b3]/20 bg-[#d769b3]/5 shadow-sm ring-1 ring-[#d769b3]/10"
            )}
            onClick={handleStepClick}
          >
            {/* Edit Pen Icon - Shows on Hover */}
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full group-hover:bg-gray-200">
                <Edit2 className="h-4 w-4 text-gray-600" />
              </div>
            </div>

            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-2 md:mb-3">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 leading-tight">
                    {step.title}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {isNextStep && (
                      <Badge className="bg-[#d769b3]/10 text-[#d769b3] border-[#d769b3]/20 text-[10px] md:text-xs">
                        {t("talent:application.next_step")}
                      </Badge>
                    )}
                    <span className="scale-90 origin-left md:scale-100">
                      {getStatusBadge(step.status)}
                    </span>
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">
                  {step.description}
                </p>
                <div className="flex items-center gap-4 text-xs md:text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3 md:h-4 md:w-4" />
                    {t("talent:application.estimated_duration", {
                      time: step.estimatedTime
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TalentApplication() {
  const { t } = useTranslation(["talent", "common"]);
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<any>(null);
  const navigate = useNavigate();
  const applicationSteps: ApplicationStep[] = [
    {
      id: "personal_info",
      title: t("talent:layout.progress_steps.personal_info"),
      description: t("talent:application.step_descriptions.personal_info"),
      status: "not_started",
      href: "/profile/personal-info",
      estimatedTime: t("talent:application.estimated_times.10_minutes")
    },
    {
      id: "german_skills",
      title: t("talent:dashboard.progress.steps.german_skills"),
      description: t("talent:application.step_descriptions.german_skills"),
      status: "not_started",
      href: "/profile/german-skills",
      estimatedTime: t("talent:application.estimated_times.1_2_months")
    },
    {
      id: "experience",
      title: t("talent:dashboard.progress.steps.experience"),
      description: t("talent:experience.description"),
      status: "not_started",
      href: "/profile/experience",
      estimatedTime: t("talent:application.estimated_times.10_minutes")
    },
    {
      id: "education",
      title: t("talent:dashboard.progress.steps.education"),
      description: t("talent:education.description"),
      status: "not_started",
      href: "/profile/education",
      estimatedTime: t("talent:application.estimated_times.5_minutes")
    }
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      if (profile.anonymous) return;

      try {
        const { data: applicationData } = await supabase
          .from("talent_profiles")
          .select("*")
          .eq("id", profile.id)
          .single();

        if (applicationData) {
          setApplication(applicationData);
        }
      } catch (error) {
        console.error("Error fetching application:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profile]);

  const getStepStatus = (stepId: string): ApplicationStep["status"] => {
    if (!application) return "not_started";

    // This would be based on actual application data
    switch (stepId) {
      case "personal_info":
        return application.given_name &&
          application.family_name &&
          application.email
          ? "completed"
          : "not_started";
      case "german_skills":
        return application.german_language_skills ? "completed" : "not_started";
      case "experience":
        if (application.work_experience) {
          try {
            return application?.work_experience?.areas_of_expertise
              ? "completed"
              : "not_started";
          } catch {
            return "not_started";
          }
        }
        return "not_started";
      case "education":
        if (
          application.education &&
          Array.isArray(application.education.entries)
        ) {
          return application.education.entries.length > 0
            ? "completed"
            : "not_started";
        }
        return "not_started";
      default:
        return "not_started";
    }
  };

  const completedSteps = applicationSteps.filter(
    (step) => getStepStatus(step.id) === "completed"
  ).length;
  const progressPercentage = (completedSteps / applicationSteps.length) * 100;

  // Find the next undone step (first step that's not completed)
  const nextStepIndex = applicationSteps.findIndex(
    (step) => getStepStatus(step.id) !== "completed"
  );
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {t("talent:application.progress")}
            <Badge variant="secondary">
              {t("talent:application.steps_completed", {
                completed: completedSteps,
                total: applicationSteps.length
              })}
            </Badge>
          </CardTitle>
          <CardDescription>
            {t("talent:application.progress_description", {
              percentage: progressPercentage.toFixed(0)
            })}
          </CardDescription>
          <Progress value={progressPercentage} className="mt-4" />
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 h-8"
              onClick={() => navigate(`/talent/${profile.id}`)}
            >
              <User className="h-4 w-4" />
              {t("talent:application.actions.view_profile")}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Application Timeline */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle>{t("talent:application.timeline_title")}</CardTitle>
          <CardDescription>
            {t("talent:application.timeline_description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-6">
          <div className="space-y-0">
            {applicationSteps.map((step, index) => {
              const currentStatus = getStepStatus(step.id);
              const stepWithStatus = { ...step, status: currentStatus };
              // Make all steps accessible for editing
              const isAccessible = true;
              const nextStepCompleted =
                index < applicationSteps.length - 1
                  ? getStepStatus(applicationSteps[index + 1].id) ===
                    "completed"
                  : false;
              const isNextStep = index === nextStepIndex;

              return (
                <TimelineStep
                  key={step.id}
                  id={step.id}
                  step={stepWithStatus}
                  stepNumber={index + 1}
                  isLast={index === applicationSteps.length - 1}
                  isAccessible={isAccessible}
                  isNextStepCompleted={nextStepCompleted}
                  isNextStep={isNextStep}
                  onEdit={() => {
                    // Handle edit action if needed
                    console.log("Edit step:", step.id);
                  }}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>
      {!profile.anonymous && (
        <FileUploader>
          <DocumentsSection
            isTalentProfile={true}
            talent_id={profile.id}
            canUploadDocuments={true}
            talentProfileData={application}
            setTalentProfileData={setApplication}
          />
        </FileUploader>
      )}
      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t("talent:application.help.title")}</CardTitle>
          <CardDescription>
            {t("talent:application.help.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button variant="outline" className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              {t("talent:application.help.visit_help_center")}
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              {t("talent:application.help.contact_support")}
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              {t("talent:application.help.view_faq")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
